// ─────────────────────────────────────────────────────────────
// stockReport.config.js — Report 3: complete stock report
// Shape: statement (landscape) — three sections
//   1. Jumbo rolls in stock   (remaining cost, same as the P&L)
//   2. Cores in hand          (in-stock pool, grouped by spec)
//   3. Finished stock         (by spec, pooled cost)
//
// Owner-only. Values use landed cost, never `rate` — rate
// excludes freight, GST and other charges. Jumbo + finished
// matches stock_value on the dashboard.
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));
const yd  = (v) => `${num(v).toLocaleString('en-PK', { maximumFractionDigits: 2 })} yd`;
const mm  = (v) => `${num(v).toLocaleString('en-PK', { maximumFractionDigits: 2 })} mm`;
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ── Section 1 — jumbo rolls ──────────────────────────────────
const jumboSection = (rolls) => {
  const rows = rolls.map((r) => ({
    roll:      r.vendor_roll_number,
    jumbo:     r.jumbo_name,
    vendor:    r.vendor_name,
    total:     yd(r.total_yards),
    consumed:  yd(r.consumed_yards),
    remaining: yd(r.remaining_yards),
  cost_per_yard: num(r.remaining_yards) > 0 ? num(r.remaining_cost) / num(r.remaining_yards) : 0,
    value: num(r.remaining_cost),
    __yards: num(r.remaining_yards),
  }));

  const totalYards = rows.reduce((s, r) => s + r.__yards, 0);
  const totalValue = rows.reduce((s, r) => s + r.value, 0);

  return {
    title: 'Jumbo Rolls in Stock',
    tone: 'net',
    columns: [
      { key: 'roll',          label: 'Roll #',    align: 'left',  fmt: 'text', width: 28 },
      { key: 'jumbo',         label: 'Jumbo',     align: 'left',  fmt: 'text' },
      { key: 'vendor',        label: 'Vendor',    align: 'left',  fmt: 'text', width: 28 },
      { key: 'total',         label: 'Received',  align: 'right', fmt: 'text', width: 24 },
      { key: 'consumed',      label: 'Used',      align: 'right', fmt: 'text', width: 24 },
      { key: 'remaining',     label: 'Remaining', align: 'right', fmt: 'text', width: 24 },
      { key: 'cost_per_yard', label: 'Cost / yd', align: 'right', fmt: 'rate', width: 20 },
      { key: 'value',         label: 'Value',     align: 'right', fmt: 'money', width: 26, bold: true },
    ],
    rows,
    footRow: [
            plural(rows.length, 'roll').toUpperCase(),
      '', '', '', '',
      yd(totalYards),
      '',
      FORMATTERS.money(totalValue),
    ],
    __value: totalValue,
  };
};

// ── Section 2 — cores in hand, grouped by tape type ──────────
const coreSection = (groups) => {
  const byType = {};
  groups.forEach((g) => {
    const key = g.tape_type_name || 'Unspecified';
    (byType[key] = byType[key] || []).push(g);
  });

  const rows = [];
  let grandCores = 0;
  let grandValue = 0;

  Object.keys(byType).sort().forEach((type) => {
    let typeCores = 0;
    let typeValue = 0;

    byType[type].forEach((g) => {
      const cores = num(g.cores_available);
      const value = num(g.total_cost);
      typeCores += cores;
      typeValue += value;

      rows.push({
        type,
        micron: `${num(g.micron_value)} µ`,
        spec: `${mm(g.core_width_mm)} × ${yd(g.core_length_yards)}`,
        oldest: g.oldest_at,
        cores,
        // Pooled average — cores of one spec are fungible
        avg_cost: cores > 0 ? value / cores : 0,
        value,
      });
    });

    rows.push({
      __subtotal: true,
      type: `${type} — Subtotal`,
      cores: typeCores,
      value: typeValue,
    });

    grandCores += typeCores;
    grandValue += typeValue;
  });

  return {
    title: 'Cores in Hand',
    tone: 'net',
    columns: [
      { key: 'type',     label: 'Tape Type',   align: 'left',  fmt: 'text' },
      { key: 'micron',   label: 'Micron',      align: 'right', fmt: 'text', width: 18 },
      { key: 'spec',     label: 'Core Spec',   align: 'left',  fmt: 'text' },
      { key: 'oldest',   label: 'Oldest',      align: 'left',  fmt: 'date', width: 24 },
      { key: 'cores',    label: 'Cores',       align: 'right', fmt: 'int',  width: 18 },
      { key: 'avg_cost', label: 'Avg / Core',  align: 'right', fmt: 'money2', width: 24 },
      { key: 'value',    label: 'Value',       align: 'right', fmt: 'money',  width: 26, bold: true },
    ],
    rows,
    footRow: [
      'TOTAL CORES',
      '', '', '',
      FORMATTERS.int(grandCores),
      '',
      FORMATTERS.money(grandValue),
    ],
    __value: grandValue,
  };
};

// ── Section 3 — finished stock ───────────────────────────────
const finishedSection = (stock) => {
  const rows = [];
  let grandQty = 0;
  let grandValue = 0;

  stock.forEach((s) => {
    const q = num(s.quantity);
    const v = num(s.total_cost);
    grandQty += q;
    grandValue += v;

    rows.push({
      type:   s.tape_type_name,
      micron: `${num(s.micron_value)} µ`,
      size:   `${mm(s.width_mm)} × ${yd(s.length_yards)}`,
      quantity: q,
      // Pooled — total_cost covers every piece of this spec
      avg_cost: q > 0 ? v / q : 0,
      price: num(s.price),
      value: v,
    });
  });

  return {
    title: 'Finished Stock',
    tone: 'net',
    columns: [
      { key: 'type',     label: 'Tape Type', align: 'left',  fmt: 'text' },
      { key: 'micron',   label: 'Micron',    align: 'right', fmt: 'text', width: 18 },
      { key: 'size',     label: 'Size',      align: 'left',  fmt: 'text' },
      { key: 'quantity', label: 'Pieces',    align: 'right', fmt: 'int',    width: 18 },
      { key: 'avg_cost', label: 'Avg Cost',  align: 'right', fmt: 'money2', width: 24 },
      { key: 'price',    label: 'Price',     align: 'right', fmt: 'money2', width: 24 },
      { key: 'value',    label: 'Value',     align: 'right', fmt: 'money',  width: 26, bold: true },
    ],
    rows,
    footRow: [
      'TOTAL FINISHED',
      '', '',
      FORMATTERS.int(grandQty),
      '', '',
      FORMATTERS.money(grandValue),
    ],
    __value: grandValue,
  };
};

// ── Main ─────────────────────────────────────────────────────
export const stockReportConfig = ({ rolls, cores, stock }) => {
  const arr = (v) => (Array.isArray(v) ? v : []);

  const s1 = jumboSection(arr(rolls));
  const s2 = coreSection(arr(cores));
  const s3 = finishedSection(arr(stock));

  const total = s1.__value + s2.__value + s3.__value;

  return {
    kind: 'statement',
    orientation: 'l',
    title: 'Stock Report',
    subtitle: `As at ${FORMATTERS.date(new Date())}`,

    boxes: [
      {
        heading: 'Stock Position',
        rows: [
                    { label: 'Jumbo Rolls',    value: plural(arr(rolls).length, 'roll') },
          { label: 'Core Specs',     value: plural(arr(cores).length, 'spec') },
          { label: 'Finished Sizes', value: plural(arr(stock).length, 'size') },
        ],
      },
      {
        heading: 'Value Summary',
        rows: [
          { label: 'Jumbo Rolls',     value: FORMATTERS.money(s1.__value) },
          { label: 'Cores',           value: FORMATTERS.money(s2.__value) },
          { label: 'Finished Stock',  value: FORMATTERS.money(s3.__value) },
          { label: 'Total Inventory', value: FORMATTERS.money(total) },
        ],
      },
    ],

    sections: [s1, s2, s3],

    totals: [
      { label: 'Jumbo Roll Value',     value: s1.__value, fmt: 'money' },
      { label: 'Core Value',           value: s2.__value, fmt: 'money' },
      { label: 'Finished Stock Value', value: s3.__value, fmt: 'money' },
      { label: 'Total Inventory',      value: total, fmt: 'money', emphasis: true, tone: 'net' },
    ],

    signatures: SIGNATURES.report,
  };
};

export default stockReportConfig;